<script setup lang="ts">
import { createApp, type App, type Ref } from 'vue';
import { createI18n, useI18n, type I18n } from '#i18n';

// components
import Forms from '~/components/redesign/Forms';

// utils
import get from 'lodash.get';
import { isHtmlEl, isString, isLocalStorageAvailable } from '~/utils';

// services
import { UserId } from '~/services';

// types
import type { SubmittedData } from '~/components/redesign/Forms/Chatlio.vue';

type Chatlio = {
  identify(userId: string, param: Record<'name' | 'email', string>): void;
};

const { locale, messages } = useI18n();
const {
  public: { chatlioId },
} = useRuntimeConfig();

const MOUNT_POINT = 'chatlio-custom-container';

enum WIDGET_CLASS {
  FORM = '.chatlio-offline-message-container',
  CHAT = '.chatlio-message-container',
  BODY = '.chatlio-widget-body',
  INPUT = '.chatlio-new-message-container',
}

enum EVENT_NAME {
  READY = 'chatlio.ready',
  EXPANDED = 'chatlio.expanded',
  COLLAPSED = 'chatlio.collapsed',
  PRE_CHAT_INFO_SUBMITTED = 'chatlio.preChatInfoSubmitted',
}

type Listener = {
  eventName: EVENT_NAME;
  handler: (e: Event) => void;
};
const listeners = ref<Listener[]>([]);

const app = shallowRef<null | App>(null);
const i18n = shallowRef<null | I18n>(null);

const collapseWidget = () => {
  if (!isLocalStorageAvailable()) return;

  const uuidKey = `chatlio_uuid--${chatlioId}`;
  const uuidValue = localStorage.getItem(uuidKey);

  if (isString(uuidValue)) {
    const isClosedKey = `chatlio-${uuidValue}-isClosed`;
    const isClosedValue = localStorage.getItem(isClosedKey);

    if (isClosedValue === 'false') {
      localStorage.setItem(isClosedKey, 'true');
    }
  }
};

const createMountEl = (parentEl: HTMLElement) => {
  const widgetBodyEl = parentEl.querySelector(WIDGET_CLASS.BODY);
  const mountPointEl = parentEl.querySelector(`#${MOUNT_POINT}`);

  if (!isHtmlEl(widgetBodyEl) || parentEl.contains(mountPointEl)) return;

  const el = document.createElement('div');
  el.setAttribute('id', MOUNT_POINT);

  widgetBodyEl.appendChild(el);

  return el;
};

const getChatlioInstance = (): Chatlio | undefined => get(window, '_chatlio');

const formSubmittedHandler = ({ fullname, email }: SubmittedData) => {
  const chatlio = getChatlioInstance();

  if (chatlio) {
    chatlio.identify(UserId.get(), {
      name: fullname,
      email,
    });
  }
};

const mountApp = (mountEl: HTMLElement) => {
  const app = createApp(Forms.Chatlio, {
    onSubmitted: formSubmittedHandler,
  });

  i18n.value = createI18n({
    legacy: false,
    locale: locale.value,
    globalInjection: true,
    messages: messages.value,
  });

  app.use(i18n.value);
  app.mount(mountEl);

  return app;
};

onMounted(() => {
  collapseWidget();

  /*
    When the widget is expanded the chatlio.expanded event is emitted.
  */
  const expandedHandler = ({ target }: Event) => {
    if (!isHtmlEl(target)) return;
    if (target.contains(document.querySelector(WIDGET_CLASS.CHAT))) return;

    const mountEl = createMountEl(target);

    if (isHtmlEl(mountEl)) {
      app.value = mountApp(mountEl);
    }
  };

  document.addEventListener(EVENT_NAME.EXPANDED, expandedHandler, {
    passive: true,
  });

  listeners.value.push({
    eventName: EVENT_NAME.EXPANDED,
    handler: expandedHandler,
  });
});

onBeforeUnmount(() => {
  listeners.value.forEach(({ eventName, handler }) => {
    document.removeEventListener(eventName, handler);
  });
});

watch(locale, newLocale => {
  if (i18n.value) {
    /*
      when vue-i18n is being used with legacy: false,
      note that i18n.global.locale is a ref, so we must set it via .value
    */
    (i18n.value.global.locale as Ref<string>).value = newLocale;
  }
});
</script>

<template>
  <chatlio-widget :widgetid="chatlioId" />
</template>

<style lang="scss">
@use '_/mixins/position';
@use '_/mixins/size';
@use '_/variables/shadow';
@use '_/fn';

// RESETS
.chatlio-widget {
  input {
    color: initial !important;
    margin-bottom: initial !important;
    resize: initial !important;
    border-radius: initial !important;
    outline: initial !important;
  }

  button {
    background-color: initial !important;
    pointer-events: initial !important;
    height: initial !important;
    width: initial !important;
    line-height: initial !important;
  }
}

#chatlio-widget * {
  user-select: auto !important;
}

.chatlio-widget-body {
  border-radius: 0 !important;
  max-width: 22.5rem;
  max-height: 33.25rem;
  box-shadow: shadow.$elevation-2 !important;

  @media only screen and (max-width: 480px) {
    top: unset !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 6rem !important;

    max-width: 100%;
  }

  .chatlio-chat-header {
    background-color: transparent !important;
  }
}

.chatlio-offline-message-container {
  display: none;
}

// close button
.chatlio-chat-header {
  &__minimize-button {
    filter: brightness(0) saturate(100%) invert(12%) sepia(7%) saturate(1113%)
      hue-rotate(169deg) brightness(91%) contrast(91%);
  }
}

// form
#chatlio-custom-container {
  @include position.absolute-full;
  padding: 5rem 2rem 4rem 2rem;
  overflow-y: auto;
}
</style>
